
import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

// Define the Category type
interface Category {
  id: string;
  name: string;
}

interface CategorySelectProps {
  categoryId: string | null;
  onCategoryChange: (value: string) => void;
  selectedCategories: string[];
  showRandomOption?: boolean;
}

export default function CategorySelect({
  categoryId,
  onCategoryChange,
  selectedCategories,
  showRandomOption = false
}: CategorySelectProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  
  useEffect(() => {
    const fetchCategories = async () => {
      if (selectedCategories.length > 0) {
        const { data, error } = await supabase
          .from('categories')
          .select('id, name')
          .in('id', selectedCategories);
          
        if (error) {
          console.error('Error fetching category details:', error);
          return;
        }
        
        // Filter out any categories with empty or null IDs
        const validCategories = (data || []).filter(cat => cat.id && cat.id.trim() !== '');
        setCategories(validCategories);
      } else {
        setCategories([]);
      }
    };
    
    fetchCategories();
  }, [selectedCategories]);
  
  return (
    <div className="w-full">
      <Select 
        value={categoryId || ""}  
        onValueChange={onCategoryChange}
        disabled={selectedCategories.length === 0}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={
            selectedCategories.length === 0 
              ? "Choose categories above" 
              : "Select a category"
          } />
        </SelectTrigger>
        <SelectContent className="select-content">
          {showRandomOption && (
            <SelectItem value="random">Random Category</SelectItem>
          )}
          {categories.map(category => (
            // Ensure we never render items with empty values
            category.id && category.id.trim() !== '' ? (
              <SelectItem key={category.id} value={category.id}>
                {category.name || "Unnamed Category"}
              </SelectItem>
            ) : null
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
